import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { ObjectAttributesEditorState } from '@/features/objects';
import { OBJECT_ID_KEY } from '@/services/db/object/entity';
import { queueObjectCreate } from '@/services/db/object/mutations';
import { getObjects } from '@/services/db/object/queries';
import type { ObjectType } from '@/services/db/object-type/entity';
import { queryEncode } from '@/utils/condition-query-encoding';
import { recordSentAttributes, reportObjectSaveError, useObjectSaveExit } from './object-save-flow';

/** The object create is queued; check once after this delay whether it landed. */
const CREATE_POLL_DELAY_MS = 3000;
const CREATE_SNACKBAR_ID = 'object.create';

type UseCreateObjectFlowOptions = {
  objectType: ObjectType;
  workspaceId: number;
  editor: ObjectAttributesEditorState;
};

/**
 * The create-object save flow: queue the create, then check once after a short
 * delay whether the object landed — navigating to its detail page when it did,
 * or back to the type list with a "will be added soon" note when it did not.
 */
export function useCreateObjectFlow({ objectType, workspaceId, editor }: UseCreateObjectFlowOptions) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const snackbars = useSnackbars();
  const [isSaving, setIsSaving] = useState(false);
  const exit = useObjectSaveExit(editor);

  const listHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug',
    params: { workspaceId, slug: objectType.slug },
  }).href;

  const findCreatedObject = async (objectId: string) => {
    const filters = queryEncode({
      and: [
        { object_attribute: { field: OBJECT_ID_KEY, value: objectId, operator: 'eq', type_id: Number(objectType.id) } },
      ],
    });
    const { objects } = await getObjects(workspaceId, { objectTypeId: objectType.id, filters }, queryClient);
    return objects[0] ?? null;
  };

  const waitForCreatedObjectAndNavigate = async (objectId: string) => {
    snackbars.create({
      variant: 'default',
      id: CREATE_SNACKBAR_ID,
      message: 'Creating your object…',
      // zero disables the auto-close
      autoCloseTimeout: 0,
    });
    await new Promise((resolve) => setTimeout(resolve, CREATE_POLL_DELAY_MS));

    try {
      const record = await findCreatedObject(objectId);
      if (record == null) throw new Error('not created yet');
      snackbars.destroy(CREATE_SNACKBAR_ID);
      exit.resetAndNavigate(
        router.buildLocation({
          to: '/workspaces/$workspaceId/objects/$slug/$objectId',
          params: { workspaceId, slug: objectType.slug, objectId: record.id },
        }).href,
      );
    } catch {
      snackbars.create({ variant: 'default', id: CREATE_SNACKBAR_ID, message: 'Your object will be added soon.' });
      exit.resetAndNavigate(listHref);
    }
  };

  /** Resolves false when the create was rejected — the error is already reported on the page. */
  const save = async (): Promise<boolean> => {
    const { attrs, hiddenAttrs } = editor.getModelAttributes();
    setIsSaving(true);
    try {
      let savedAttributes;
      try {
        savedAttributes = await queueObjectCreate({
          workspaceId,
          objectTypeId: objectType.id,
          objectId: attrs[OBJECT_ID_KEY] ?? '',
          attributes: attrs,
          hiddenAttributes: hiddenAttrs,
        });
      } catch (error) {
        reportObjectSaveError({ error, editor, snackbars, verb: 'create' });
        return false;
      }

      recordSentAttributes(queryClient, workspaceId, objectType.id, savedAttributes);
      await waitForCreatedObjectAndNavigate(attrs[OBJECT_ID_KEY] ?? '');
      return true;
    } finally {
      setIsSaving(false);
    }
  };

  const discard = () => {
    exit.resetAndNavigate(listHref);
  };

  return { isSaving, save, discard, hasUnsavedChanges: exit.hasUnsavedChanges };
}
